/* eslint-disable indent */
import cx from 'classnames'
import { Price } from 'components/common/Price'
import { Currencies } from 'constants/currencies'
import getSymbolFromCurrency from 'currency-symbol-map'
import { Convert } from 'easy-currencies'
import { PropsWithChildren, useEffect, useState } from 'react'

const initialCurrencies = ['GBP', 'EUR', 'USD']

type Props = {
  price?: number
  currency?: string
} & PropsWithChildren

export const CurrencyDisplay = ({
  children,
  price,
  currency = 'GBP',
}: Props) => {
  const [currencies, setCurrencies] = useState<string[]>(initialCurrencies)
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(
    currency || 'GBP'
  )
  const [currentCurrencyPrice, setCurrentCurrencyPrice] = useState<
    number | undefined
  >(price)

  useEffect(() => {
    if (
      !currency ||
      !selectedCurrency ||
      typeof price === 'undefined' ||
      isNaN(price)
    )
      return

    if (currency === selectedCurrency) {
      setCurrentCurrencyPrice(price)
      return
    }

    const fetchCurrency = async () => {
      const convert = await Convert(price).from(currency).to(selectedCurrency)

      if (!isNaN(convert)) setCurrentCurrencyPrice(convert)
    }
    fetchCurrency()
  }, [selectedCurrency, price, currency])

  useEffect(() => {
    if (!currency) return
    if (!currencies.includes(currency)) {
      setCurrencies([currency, ...initialCurrencies])
    }
    setSelectedCurrency(currency)
  }, [currencies, currency])

  return (
    <div className='flex items-center space-x-2'>
      {currentCurrencyPrice && (
        <p>
          {/* NOTE: The backend price is in pence, but the frontend is in pounds */}
          <Price
            formattingOptions={{
              currency: selectedCurrency,
              to: Currencies.USER,
              from: Currencies.USER,
            }}
          >
            {currentCurrencyPrice / 100}
          </Price>
        </p>
      )}

      {children}

      <div className='flex items-center'>
        {currencies.map((newCurrency, index) => (
          <div
            key={index}
            onClick={() => setSelectedCurrency(newCurrency)}
            className={cx(
              'border border-x-0 border-solid border-cool-400 py-1.5',
              {
                'border-l': index === 0,
                'border-r': index === currencies.length - 1,
                '!border border-cool-900': selectedCurrency === newCurrency,
              }
            )}
          >
            <p
              className={cx(
                'cursor-pointer border-r border-cool-400 px-2 text-sm leading-none text-cool-900',
                {
                  'border-l-0': index === 0,
                  '!border-r-0': index === currencies.length - 1,
                }
              )}
            >
              {getSymbolFromCurrency(newCurrency)}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
